import { addDays, format, isAfter, isBefore, isSameDay } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Keyboard,
  Linking,
  Modal,
  Platform,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View
} from 'react-native';
import styled from 'styled-components/native';
import Colors from '../../../constants/Colors';
import { useStudyEventFormDataSourceContext } from '../../../screens/study/StudyEventFormDataSource';
import { WearableType, WearableHelper } from '../../../tools/WearableHelper';
import { WearableResult } from '../../../tools/WearableHelper.native';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import * as WebBrowser from 'expo-web-browser';
import { useAuthRequest } from 'expo-auth-session';
import Button from '../../../components/Button';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  fitbitDiscovery,
  getFitbitAuthRequestConfig,
  getFitbitAuthProps
} from '../../../tools/FitbitHelper';
import { useUserContext } from '../../../context/UserContext';
import { useUpdateUser } from '../../../hooks/user/useUpdateUser';
import { DataPointOccurrence } from '../../../tools/DataPointOccrence';
import Spacer from '../../../components/Spacer';
import { B } from '../../../components/Typography';
import IconButton from '../../../components/IconButton';
import { Feather } from '@expo/vector-icons';
import { requestToken } from '../../../tools/GarminHelper';
import { User } from '../../../models/User';
import elementIDs from '../../../../elementIDs';
import { useFindUserAttributes } from '../../../hooks/user/useFindUserAttributes';
import {
  UserAttribute,
  UserAttributeInputType,
  UserAttributeSource,
  UserAttributeType
} from '../../../models/UserAttribute';
import HoursField from '../../../components/form/HoursField';
import { getOuraAuthRequestConfig, useOura } from '../../../tools/OuraHelper';
import WebViewModal from '../../../screens/modals/WebViewModal';
import { GoogleHealthConnect } from '../../../tools/google-health-connect';
import {
  checkPhysicalActivityPermission,
  showPhysicalActivityAlert
} from '../../../tools/google-fit.native';
import { useDeviceSyncContext } from '../../../context/DeviceSyncContext';

WebBrowser.maybeCompleteAuthSession();

const FormWearableFieldView = styled.View`
  width: 100%;
`;

const StyledTextInput = styled.TextInput`
  font-family: 'WorkSans-SemiBold';
  min-height: auto;
  min-width: 0;
  max-width: 100%;
  /* This is a work around for Android to avoid getting text cropped as you type */
  ${Platform.OS === 'android'
    ? 'font-size: 14.5px; font-weight: normal; letter-spacing: -0.3px;'
    : ''}
`;

const StyledText = styled.Text`
  font-family: 'WorkSans-SemiBold';
  margin-left: 3px;
  min-width: 1px;
  ${Platform.OS === 'android'
    ? 'font-size: 14.5px; font-weight: normal; letter-spacing: -0.3px;'
    : ''};
`;

const StyledContainer = styled.View<{
  hasHelperText?: boolean;
  minHeight?: number;
  error?: boolean;
}>`
  align-items: center;
  flex-direction: row;
  width: 100%;
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  ${Platform.OS !== 'web' ? 'padding-right: 50px;' : ''}
`;

const HelperText = styled.Text`
  font-family: 'WorkSans-SemiBold';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.error};
  margin: 4px 4px 12px 4px;
`;

const StyledBox = styled.View<{ touched?: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 14px;
  border: 1px solid
    ${p => (p.touched ? Colors.primaryPurple : Colors.primaryPurple)};
  background-color: ${p => (p.touched ? Colors.primaryPurple : 'white')};
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  box-shadow: none;
`;

const ListItemText = styled.Text`
  font-family: 'WorkSans-SemiBold';
  font-size: 13px;
  line-height: 16px;
  color: ${Colors.mediumText};
  ${Platform.OS !== 'android' ? 'padding-top: 2px;' : ''};
`;

const StyledTouchable = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
`;

const StyledIconsContainer = styled.View`
  flex-direction: row;
  width: 100%;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`;

const StyledIconTextContainer = styled.View`
  margin-bottom: 10px;
  width: 100%;
`;

const StyledIcon = styled.Image<{ selected: boolean }>`
  height: 40px;
  width: 40px;
  border-radius: 10px;
  margin-right: 10px;
  border-width: ${p => (p.selected ? 2 : 0.2)}px;
  border-color: ${p => (p.selected ? Colors.primaryPurple : 'gray')};
`;

const StyledFitbitIconWithWordMark = styled.Image`
  height: 30px;
  width: 110px;
`;

const StyledOuraconWithWordMark = styled.Image`
  height: 35px;
  width: 100px;
`;

const StyledGoogleFitIconWithWordMark = styled.Image`
  height: 30px;
  width: 140px;
`;

const StyledHealthConnectIconWithWordMark = styled.Image`
  height: 40px;
  width: 190px;
`;

const StyledGarminIconWithWordMark = styled.Image`
  height: 40px;
  width: 140px;
`;

const StyledFitbitIcon = styled(StyledIcon)<{ selected: boolean }>`
  margin-right: 10px;
  border-width: 2px;
  border-color: ${p => (p.selected ? Colors.primaryPurple : 'transparent')};
`;

const StyledGarminIcon = styled(StyledIcon)<{ selected: boolean }>`
  height: 38px;
  width: 38px;
  margin-right: 10px;
  border-width: 2px;
  border-color: ${p => (p.selected ? Colors.primaryPurple : 'transparent')};
`;

const StyledItemContainer = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  justify-content: space-between;
`;

const StyledSelectedIndicator = styled.View<{ selected: boolean }>`
  height: 18px;
  width: 18px;
  background-color: ${p => (p.selected ? Colors.primaryPurple : 'transparent')};
  border-color: ${Colors.primaryPurple};
  border-width: 1px;
  border-radius: 18px;
  margin: 0 15px 0 0;
`;

const StyledTouchableIcon = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
`;

const StyledDimContainer = styled.View`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const StyledDialog = styled.View`
  border-radius: 10px;
  overflow: hidden;
  background-color: white;
  width: 100%;
  padding: 20px 20px 30px 20px;
  align-items: center;
`;

const StyledLogo = styled.Image`
  height: 70px;
`;

const StyledSignInImage = styled.Image`
  height: 50px;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
`;

// Daily totals are stored as separate samples. Other readings use the latest.
const TOTAL_DATA_SOURCES = new Set([
  'Step Count',
  'Active Energy Burned',
  'Basal Energy Burned',
  'Stand Time',
  'Energy Consumed',
  'Calories Consumed',
  'Protein Consumed',
  'Total Fat Consumed',
  'Carbohydrates',
  'Flights Climbed',
  'Walking/Running Distance',
  'Swimming Distance',
  'Cycling Distance',
  'Calories Burned',
  'Nutrition'
]);

function attributeQueryRange(
  occurrence: DataPointOccurrence | undefined,
  intendedStartDate?: Date,
  dataSource?: string
) {
  const today = new Date();
  const day =
    occurrence === DataPointOccurrence.Yesterday
      ? addDays(today, -1)
      : occurrence === DataPointOccurrence.Intended_Start_Date
        ? intendedStartDate || today
        : occurrence === DataPointOccurrence.Today
          ? today
          : undefined;
  if (day) {
    // Sleep starts the evening before the morning it ends.
    const start = dataSource === 'Sleep' ? addDays(day, -1) : day;
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(day, 'yyyy-MM-dd')
    };
  }
  return {
    startDate: format(addDays(today, -30), 'yyyy-MM-dd'),
    endDate: format(today, 'yyyy-MM-dd')
  };
}

function samplesForOccurrence(
  attributes: UserAttribute[],
  occurrence: DataPointOccurrence | undefined,
  intendedStartDate?: Date
) {
  const dated = attributes.filter(
    attribute =>
      attribute.endDate &&
      (attribute.numberValue != null || attribute.textValue)
  );
  if (!dated.length || !occurrence) {
    return [];
  }
  const newest = [...dated].sort(
    (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
  );
  const day =
    occurrence === DataPointOccurrence.Last_Recorded
      ? new Date(newest[0].endDate)
      : occurrence === DataPointOccurrence.Yesterday
        ? addDays(new Date(), -1)
        : occurrence === DataPointOccurrence.Intended_Start_Date
          ? intendedStartDate || new Date()
          : new Date();
  return newest.filter(attribute =>
    isSameDay(new Date(attribute.endDate), day)
  );
}

function samplesForSleepNight(
  attributes: UserAttribute[],
  occurrence: DataPointOccurrence | undefined,
  intendedStartDate?: Date
) {
  const dated = attributes.filter(
    attribute => attribute.startDate && attribute.endDate
  );
  if (!dated.length || !occurrence) {
    return [];
  }
  const newest = [...dated].sort(
    (a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
  );
  let minStart =
    occurrence === DataPointOccurrence.Last_Recorded
      ? new Date(newest[0].endDate)
      : occurrence === DataPointOccurrence.Intended_Start_Date
        ? new Date(intendedStartDate || new Date())
        : new Date();
  minStart.setHours(17, 59, 59, 59);
  minStart = addDays(
    minStart,
    occurrence === DataPointOccurrence.Yesterday ? -2 : -1
  );
  const maxStart = addDays(minStart, 1);
  const maxEnd = addDays(minStart, 2);
  maxEnd.setHours(0, 0, 0, 0);
  return newest.filter(attribute => {
    const start = new Date(attribute.startDate);
    const end = new Date(attribute.endDate);
    return (
      isAfter(start, minStart) &&
      isBefore(start, maxStart) &&
      isBefore(end, maxEnd)
    );
  });
}

function storedAttributeType(dataSource: string): string {
  if (dataSource === 'Step Count') return UserAttributeType.Steps;
  if (dataSource === 'HRV') return UserAttributeType.HeartRateVariability;
  return dataSource;
}

function largestAppTotal(
  samples: UserAttribute[],
  include: (attribute: UserAttribute) => boolean = () => true
): number | undefined {
  const totalsByApp = new Map<string, number>();
  for (const attribute of samples) {
    if (!include(attribute)) continue;
    const app = attribute.subSource || 'none';
    totalsByApp.set(
      app,
      (totalsByApp.get(app) || 0) + Number(attribute.numberValue || 0)
    );
  }
  if (!totalsByApp.size) return undefined;
  return Math.max(...totalsByApp.values());
}

function valueForOccurrence(
  attributes: UserAttribute[],
  occurrence: DataPointOccurrence | undefined,
  dataSource: string | undefined,
  intendedStartDate?: Date,
  dataCategory?: string
): number | string | undefined {
  const samples =
    dataSource === 'Sleep'
      ? samplesForSleepNight(attributes, occurrence, intendedStartDate)
      : samplesForOccurrence(attributes, occurrence, intendedStartDate);
  if (!samples.length) {
    return undefined;
  }
  if (
    dataSource === 'Sleep' &&
    samples.some(sample => sample.source === UserAttributeSource.HealthKit)
  ) {
    // iPhone sleep is stored as short stage segments. ASLEEP means the night's
    // asleep stages, not a single segment and not time awake.
    const category = dataCategory?.toUpperCase();
    return largestAppTotal(samples, sample => {
      if (sample.source !== UserAttributeSource.HealthKit) return false;
      const stage =
        typeof sample.jsonValue?.value === 'string'
          ? sample.jsonValue.value.toUpperCase()
          : '';
      if (category === 'ASLEEP') {
        return ['CORE', 'REM', 'DEEP', 'ASLEEP'].includes(stage);
      }
      if (category) return stage === category;
      return stage !== 'AWAKE' && stage !== 'INBED';
    });
  }
  const latest = samples[0];
  if (latest.numberValue == null) {
    return latest.textValue;
  }
  if (dataSource && TOTAL_DATA_SOURCES.has(dataSource)) {
    // Samples from one app are pieces of that day's total. Another app's
    // samples are usually the same steps again, so keep the largest app total.
    return largestAppTotal(samples);
  }
  return Number(latest.numberValue);
}

export interface FormWearableData {
  value: number | string;
  startDate: Date;
  endDate: Date;
  selected: boolean;
  granularity?: any;
}

type WearableConfig = {
  source?: string;
  dataSource: string;
  dataPointOccurrence: DataPointOccurrence;
  unit: string;
};

function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(function (word: string) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export const FormWearable = <T extends object>({
  element
}: FormElementProps<T, FormWearableConfig>) => {
  const { formValues, setFormValue } = useDynamicFormContext<
    T,
    FormWearableConfig
  >();
  const config = getOuraAuthRequestConfig();
  const { intendedStartDate } = useStudyEventFormDataSourceContext();
  const key = (element.dataSource as keyof T) || element.key;
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState(formValues[key] as any);
  const [wearableConfig, setWearableConfig] = useState<WearableConfig>();
  const [wearableResult, setWearableResult] = useState<WearableResult>();
  const [wearableResults, setWearableResults] = useState<WearableResult[]>();
  const previousValueRef = useRef(value);
  const [textWidth, setTextWidth] = useState(1);
  const textInputRef = useRef<any>();
  const [isLoading, setLoading] = useState<boolean>();
  const [error, setError] = useState<string>();
  const [helperText, setHelperText] = useState<string>();
  const [rawData, setRawData] = useState<
    string | number | Array<FormWearableData>
  >();
  // refresh value if formValues change outside of the component
  const [fitbitAuthorized, setFitbitAuthorized] = useState<boolean>();
  const {
    ouraAccessToken,
    connectOura,
    ouraAuthUrl,
    handleOuraAuthResult,
    loading: ouraLoading
  } = useOura();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const [fitbitLoading, setFitbitLoading] = useState<boolean>();
  const [garminAuthorized, setGarminAuthorized] = useState<boolean>();
  const oAuthProps = getFitbitAuthProps();
  const [request, response, promptAsync] = useAuthRequest(
    getFitbitAuthRequestConfig(oAuthProps.clientId, oAuthProps.redirectUri),
    fitbitDiscovery
  );
  const { user, setUser, studyParticipants } = useUserContext();
  const { updatedUser, updateUser, clearUpdatedUser } = useUpdateUser();
  const [showConnectModal, setShowConnectModal] = useState<boolean>();
  const { findUserAttributes } = useFindUserAttributes();
  const [openAndroidOuraConnect, setOpenAndroidAuraConnect] = useState(false);
  const { setGoogleFitPermissionRequestInprogress } = useDeviceSyncContext();

  useEffect(() => {
    if (
      !focused &&
      formValues[key] &&
      previousValueRef.current !== formValues[key]
    ) {
      setValue(formValues[key]);
      previousValueRef.current = formValues[key];
    }
  }, [focused, formValues, key]);
  const {
    healthKit,
    googleFit,
    healthConnect,
    fitbit,
    garmin,
    oura,
    rawDataSource,
    dataType
  } = element.config || {};
  const hasFallback =
    (fitbit?.dataSource &&
      ((Platform.OS === 'ios'
        ? healthKit?.dataSource
        : Platform.OS === 'android'
          ? (googleFit?.dataSource ?? healthConnect?.dataSource)
          : false) ||
        garmin?.dataSource ||
        oura?.dataSource)) ||
    (garmin?.dataSource &&
      ((Platform.OS === 'ios'
        ? healthKit?.dataSource
        : Platform.OS === 'android'
          ? (googleFit?.dataSource ?? healthConnect?.dataSource)
          : false) ||
        oura?.dataSource)) ||
    (oura?.dataSource &&
      (Platform.OS === 'ios'
        ? healthKit?.dataSource
        : Platform.OS === 'android'
          ? (googleFit?.dataSource ?? healthConnect?.dataSource)
          : false));

  // save raw data from HealthKit, GoogleFit, or Fitbit if rawDataSource is provided
  useEffect(() => {
    if (rawDataSource) {
      setFormValue(rawDataSource as keyof T, rawData as any);
    }
    // eslint-disable-next-line
  }, [rawData, rawDataSource]);

  const initWearHelper = useCallback(
    (wearableType: WearableType) => {
      if (!navigation.isFocused() || !user?.id) return;
      setLoading(true);
      let wearableHelper: WearableHelper | undefined = undefined;
      setRawData(undefined);
      setWearableResult(undefined);
      setFormValue(key, undefined as any, element);
      setValue('');
      setError(undefined);
      setHelperText(undefined);
      if (wearableType === WearableType.OURA) {
        setWearableConfig({
          source: 'Oura',
          ...oura
        } as WearableConfig);
        wearableHelper = new WearableHelper(
          WearableType.OURA,
          oura?.dataSource || '',
          oura?.dataPointOccurrence || DataPointOccurrence.Last_Recorded,
          user.id,
          oura?.category,
          oura?.unit,
          intendedStartDate
        );
      } else if (wearableType === WearableType.GARMIN) {
        setWearableConfig({
          source: 'Garmin',
          ...garmin
        } as WearableConfig);
        wearableHelper = new WearableHelper(
          WearableType.GARMIN,
          garmin?.dataSource || '',
          garmin?.dataPointOccurrence || DataPointOccurrence.Last_Recorded,
          user.id,
          garmin?.category,
          garmin?.unit,
          intendedStartDate
        );
      } else if (wearableType === WearableType.FITBIT) {
        setWearableConfig({
          source: 'Fitbit',
          ...fitbit
        } as WearableConfig);
        wearableHelper = new WearableHelper(
          WearableType.FITBIT,
          fitbit?.dataSource || '',
          fitbit?.dataPointOccurrence || DataPointOccurrence.Last_Recorded,
          user.id,
          fitbit?.category,
          fitbit?.unit,
          intendedStartDate
        );
      } else if (
        Platform.OS === 'android' &&
        wearableType === WearableType.GOOGLE_FIT
      ) {
        wearableHelper = new WearableHelper(
          WearableType.GOOGLE_FIT,
          googleFit?.dataSource || '',
          googleFit?.dataPointOccurrence || DataPointOccurrence.Last_Recorded,
          user.id,
          googleFit?.category,
          googleFit?.unit,
          intendedStartDate
        );
        setWearableConfig({
          source: 'Google Fit',
          ...googleFit
        } as WearableConfig);
      } else if (
        Platform.OS === 'android' &&
        wearableType === WearableType.HEALTH_CONNECT
      ) {
        wearableHelper = new WearableHelper(
          WearableType.HEALTH_CONNECT,
          healthConnect?.dataSource || '',
          healthConnect?.dataPointOccurrence ||
            DataPointOccurrence.Last_Recorded,
          user.id,
          healthConnect?.category,
          healthConnect?.unit,
          intendedStartDate
        );
        setWearableConfig({
          source: 'Health Connect',
          ...healthConnect
        } as WearableConfig);
      } else if (
        Platform.OS === 'ios' &&
        wearableType === WearableType.HEALTH_KIT
      ) {
        wearableHelper = new WearableHelper(
          WearableType.HEALTH_KIT,
          healthKit?.dataSource || '',
          healthKit?.dataPointOccurrence || DataPointOccurrence.Last_Recorded,
          user.id,
          healthKit?.category,
          healthKit?.unit,
          intendedStartDate
        );
        setWearableConfig({
          source: 'HealthKit',
          ...healthKit
        } as WearableConfig);
      }
      return wearableHelper;
    },
    [
      navigation,
      user?.id,
      key,
      element,
      oura,
      intendedStartDate,
      garmin,
      fitbit,
      googleFit,
      healthKit
    ]
  );

  const clearValues = useCallback(() => {
    setWearableConfig({ unit: wearableConfig?.unit || '' } as WearableConfig);
    setWearableResult(undefined);
    setValue('');
    setFormValue(key, undefined as any, element);
    setRawData(undefined);
    setError(undefined);
    // eslint-disable-next-line
  }, [element, key, wearableConfig?.unit]);

  const fetchWearableData = useCallback(
    async (
      fallback?: boolean,
      wearHelper?: WearableHelper,
      userPreferred?: boolean,
      wearableType?: WearableType
    ) => {
      setLoading(true);
      let wearableHelper: WearableHelper | undefined = undefined;

      if (wearHelper) {
        wearableHelper = wearHelper;
      } else if (!wearableType && fitbit?.dataSource && !fallback) {
        wearableHelper = await initWearHelper(WearableType.FITBIT);
      } else if (
        wearableType === WearableType.GARMIN ||
        (garmin?.dataSource && !fallback && wearableType !== WearableType.OURA)
      ) {
        wearableHelper = await initWearHelper(WearableType.GARMIN);
      } else if (
        wearableType === WearableType.OURA ||
        (oura?.dataSource && !fallback)
      ) {
        wearableHelper = await initWearHelper(WearableType.OURA);
      } else if (Platform.OS === 'android' && googleFit?.dataSource) {
        wearableHelper = await initWearHelper(WearableType.GOOGLE_FIT);
      } else if (Platform.OS === 'android' && healthConnect?.dataSource) {
        wearableHelper = await initWearHelper(WearableType.HEALTH_CONNECT);
      } else if (Platform.OS === 'ios' && healthKit?.dataSource) {
        wearableHelper = await initWearHelper(WearableType.HEALTH_KIT);
      }
      if (!navigation.isFocused()) return;
      if (wearableHelper) {
        const representsPediatricParticipant = (studyParticipants || []).some(
          studyParticipant => studyParticipant.userId !== user?.id
        );
        const useStoredProfile = representsPediatricParticipant && !!user?.id;
        if (!useStoredProfile && Platform.OS === 'android' && !userPreferred) {
          if (wearableHelper.wearableType === WearableType.GOOGLE_FIT) {
            const connected = await AsyncStorage.getItem(
              'google-fit-connected'
            );
            if (!connected) {
              clearValues();
              setLoading(false);
              return;
            }
          } else if (
            wearableHelper.wearableType === WearableType.HEALTH_CONNECT
          ) {
            const connected = await AsyncStorage.getItem(
              'first-time-permission-check'
            );
            if (!connected) {
              clearValues();
              setLoading(false);
              return;
            }
          }
        }

        try {
          if (
            !useStoredProfile &&
            user?.id &&
            Platform.OS === 'android' &&
            wearableHelper.wearableType === WearableType.GOOGLE_FIT
          ) {
            const authorized = await wearableHelper?.authorize();
            if (authorized) {
              setGoogleFitPermissionRequestInprogress(true);
              const permission = await checkPhysicalActivityPermission();
              if (permission !== 'granted') {
                setLoading(false);
                clearValues();
                setShowConnectModal(false);
                setGoogleFitPermissionRequestInprogress(false);
                await showPhysicalActivityAlert();
                return;
              }
              setGoogleFitPermissionRequestInprogress(false);
            } else {
              setLoading(false);
              return;
            }
          }
          let result: any;
          if (useStoredProfile && user?.id) {
            const queryRange = attributeQueryRange(
              wearableHelper.dataPointOccurrence,
              intendedStartDate,
              wearableHelper.dataSource
            );
            const profileAttributes = await findUserAttributes(
              user.id,
              storedAttributeType(wearableHelper.dataSource),
              wearableHelper.wearableType,
              undefined,
              undefined,
              queryRange.startDate,
              queryRange.endDate
            );
            result = valueForOccurrence(
              (profileAttributes || []).filter(
                attribute =>
                  attribute.inputType !== UserAttributeInputType.Manual
              ),
              wearableHelper.dataPointOccurrence,
              wearableHelper.dataSource,
              intendedStartDate,
              wearableHelper.dataCategory
            );
          } else {
            result =
              wearableHelper.wearableType === WearableType.GARMIN &&
              !user?.garminConfig
                ? undefined
                : await wearableHelper.getData();
          }
          if (Platform.OS === 'android' && result !== false) {
            setShowConnectModal(false);
          }
          const intendedDate =
            wearableHelper.dataPointOccurrence ===
            DataPointOccurrence.Intended_Start_Date
              ? intendedStartDate || new Date()
              : wearableHelper.dataPointOccurrence ===
                  DataPointOccurrence.Yesterday
                ? addDays(new Date(), -1)
                : wearableHelper.dataPointOccurrence ===
                    DataPointOccurrence.Today
                  ? new Date()
                  : undefined;

          let manualInputUserAttribute: UserAttribute | undefined = undefined;
          if (wearableHelper && user) {
            const startDate = format(addDays(new Date(), -30), 'yyyy-MM-dd');
            const endDate = format(new Date(), 'yyyy-MM-dd');
            const manualInputtedValues = await findUserAttributes(
              user.id,
              storedAttributeType(wearableHelper.dataSource),
              wearableHelper.wearableType,
              wearableHelper.dataPointOccurrence !==
                DataPointOccurrence.Last_Recorded
                ? UserAttributeInputType.Manual
                : undefined,
              UserAttributeInputType.Manual,
              startDate,
              endDate
            );

            if (manualInputtedValues && manualInputtedValues.length > 0) {
              if (
                manualInputtedValues[0].inputType ===
                UserAttributeInputType.Manual
              ) {
                manualInputUserAttribute = manualInputtedValues[0];
                if (
                  wearableHelper.dataPointOccurrence !==
                    DataPointOccurrence.Last_Recorded &&
                  intendedDate
                ) {
                  manualInputUserAttribute = manualInputtedValues.find(ua =>
                    isSameDay(ua.endDate, intendedDate)
                  );
                }
              }
            }
          }

          if (manualInputUserAttribute) {
            setValue(`${manualInputUserAttribute?.numberValue || ''}`);
            setFormValue(
              key,
              manualInputUserAttribute?.numberValue as any,
              element
            );
            setRawData(result || manualInputUserAttribute?.numberValue);
          } else {
            if (typeof result === 'number' || typeof result === 'string') {
              setValue(`${result ? result : ''}`);
              setFormValue(key, result as any, element);
              setRawData(result);
            } else {
              // result is WearableResult type
              if (
                !representsPediatricParticipant &&
                !userPreferred &&
                !wearHelper &&
                hasFallback &&
                !result &&
                !fallback
              ) {
                if (
                  garmin?.dataSource &&
                  wearableHelper?.wearableType === WearableType.FITBIT
                ) {
                  fetchWearableData(
                    undefined,
                    undefined,
                    undefined,
                    WearableType.GARMIN
                  );
                } else if (
                  oura?.dataSource &&
                  (wearableHelper?.wearableType === WearableType.GARMIN ||
                    wearableHelper?.wearableType === WearableType.FITBIT)
                ) {
                  fetchWearableData(
                    undefined,
                    undefined,
                    undefined,
                    WearableType.OURA
                  );
                } else {
                  fetchWearableData(true);
                }
                return;
              }

              let defaultResult = result;
              if (result?.length) {
                setWearableResults(result);
                defaultResult = result[0];
              }

              if (
                wearableHelper?.dataSource === 'Blood Glucose' &&
                dataType === 'text'
              ) {
                const value = defaultResult?.data
                  ? defaultResult?.data
                      ?.map((d: any) => Number(d.value.toFixed(2)))
                      .join(', ')
                  : undefined;
                setValue(value || '');
                setFormValue(key, value as any, element);
              } else {
                setValue(`${defaultResult?.value ? defaultResult?.value : ''}`);
                setFormValue(key, defaultResult?.value as any, element);
              }

              if (!result) {
                let noDataFoundMessage = undefined;
                switch (wearableHelper?.wearableType) {
                  case WearableType.OURA:
                    noDataFoundMessage =
                      'No data found in the selected device. Try syncing your wearable to their app. (i.e. Oura ring syncing to Oura app).';
                    break;

                  case WearableType.GARMIN:
                    noDataFoundMessage =
                      'No data found in the selected device. Try syncing your wearable to their app. (i.e. Garmin device syncing to Garmin Connect).';
                    break;

                  case WearableType.FITBIT:
                    noDataFoundMessage =
                      'No data found in the selected device. Try syncing your wearable to their app. (i.e. Fitbit syncing to the Fitbit app).';
                    break;

                  case WearableType.HEALTH_KIT:
                    noDataFoundMessage =
                      'No data found in the selected device. Check Apple Health to verify the data has been synced or permission has been granted.';
                    break;

                  case WearableType.GOOGLE_FIT:
                    noDataFoundMessage =
                      'No data found in the selected device. Check Google Fit to verify the data has been synced.';
                    break;
                }
                setHelperText(noDataFoundMessage);
              }

              setWearableResult(defaultResult);
              if (defaultResult?.data) {
                const rawData = defaultResult.data?.map((data: any) => ({
                  ...data,
                  selected: data?.sleepStage?.toLowerCase() !== 'awake'
                }));
                setRawData(rawData);
              }
            }
          }
          setLoading(false);
        } catch (error: any) {
          if (
            wearableHelper?.wearableType === WearableType.FITBIT &&
            fitbit?.dataSource
          ) {
            setError(`${error}`);
            if (
              error === 'Fitbit account connection has expired.' ||
              error === 'Fitbit account not connected.'
            ) {
              if (user) {
                setUser({ ...user, fitbitAccessToken: undefined });
              }
            }
            if (!userPreferred && !wearHelper && hasFallback && !fallback) {
              if (garmin?.dataSource) {
                fetchWearableData(
                  undefined,
                  undefined,
                  undefined,
                  WearableType.GARMIN
                );
              } else if (oura?.dataSource) {
                fetchWearableData(
                  undefined,
                  undefined,
                  undefined,
                  WearableType.OURA
                );
              } else {
                fetchWearableData(true);
              }
              return;
            } else if (!hasFallback && Platform.OS === 'android') {
              clearValues();
            }
          } else if (
            wearableHelper?.wearableType === WearableType.GARMIN &&
            garmin?.dataSource &&
            error?.message
          ) {
            const errorMessage: string = error.message;
            if (errorMessage.includes('required HEALTH_EXPORT')) {
              setError(
                `This application does not have permission to access Daily Health Stats, reconnect Garmin account and allow permission.`
              );
            } else {
              setError('Garmin account not connected.');
              setGarminAuthorized(false);
            }
            if (!userPreferred && !wearHelper && hasFallback && !fallback) {
              if (oura?.dataSource) {
                fetchWearableData(
                  undefined,
                  undefined,
                  undefined,
                  WearableType.OURA
                );
              } else if (Platform.OS === 'android' && !googleFit?.dataSource) {
                setError(undefined);
                clearValues();
              } else {
                fetchWearableData(true);
              }
              return;
            } else if (!hasFallback && Platform.OS === 'android') {
              clearValues();
            }
          } else if (
            wearableHelper?.wearableType === WearableType.OURA &&
            oura?.dataSource &&
            error?.message
          ) {
            const errorMessage: string = error.message;
            setError(errorMessage);
            if (!userPreferred && !wearHelper && hasFallback && !fallback) {
              fetchWearableData(true);
              return;
            } else if (!hasFallback && Platform.OS === 'android') {
              clearValues();
            }
          } else if (wearableHelper?.wearableType === WearableType.HEALTH_KIT) {
            setHelperText(
              'No data found in the selected device. Check Apple Health to verify the data has been synced or permission has been granted.'
            );
          }
          setValue('');
          setFormValue(key, undefined as any, element);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    },
    [
      clearValues,
      dataType,
      element,
      findUserAttributes,
      fitbit?.dataSource,
      garmin?.dataSource,
      googleFit?.dataSource,
      hasFallback,
      healthKit?.dataSource,
      initWearHelper,
      intendedStartDate,
      key,
      navigation,
      oura?.dataSource,
      setUser,
      studyParticipants,
      user
    ]
  );

  useEffect(() => {
    if (response) {
      if (response?.type === 'success') {
        const { access_token } = response.params;
        setFitbitAuthorized(true);
        updateUser({ fitbitAccessToken: access_token });
      } else {
        setFitbitLoading(false);
      }
    } else {
      setFitbitLoading(false);
      fetchWearableData();
    }
  }, [response]);

  useEffect(() => {
    setFitbitAuthorized(Boolean(user?.fitbitAccessToken));
    setGarminAuthorized(Boolean(user?.garminConfig));
  }, [user]);

  const handleGarminConnectSuccess = useCallback(() => {
    setShowConnectModal(false);
    setGarminAuthorized(true);
    fetchWearableData(false, initWearHelper(WearableType.GARMIN));
  }, [fetchWearableData, initWearHelper]);

  useEffect(() => {
    if (updatedUser) {
      clearUpdatedUser();
      setFitbitLoading(false);
      if (Platform.OS === 'android') setShowConnectModal(false);
      fetchWearableData(
        false,
        initWearHelper(WearableType.FITBIT),
        Platform.OS === 'android'
      );
      setUser(updatedUser);
    }
  }, [
    updatedUser,
    setUser,
    clearUpdatedUser,
    initWearHelper,
    fetchWearableData
  ]);

  useEffect(() => {
    if (!navigation.isFocused() || !error) {
      return;
    }
    if (wearableConfig?.source === WearableType.FITBIT && fitbitAuthorized) {
      fetchWearableData(false, initWearHelper(WearableType.FITBIT), false);
    } else if (
      wearableConfig?.source === WearableType.OURA &&
      ouraAccessToken
    ) {
      fetchWearableData(false, initWearHelper(WearableType.OURA), false);
    } else if (
      wearableConfig?.source === WearableType.GARMIN &&
      garminAuthorized
    ) {
      fetchWearableData(false, initWearHelper(WearableType.GARMIN), false);
    }
  }, [
    fetchWearableData,
    initWearHelper,
    navigation,
    ouraAccessToken,
    fitbitAuthorized,
    wearableConfig?.source,
    error,
    garminAuthorized
  ]);

  return (
    <View>
      <FormElementQuestion element={element} />
      {wearableConfig?.dataSource === UserAttributeType.Sleep ? (
        <>
          <HoursField
            textAlign='left'
            isLoading={isLoading}
            value={value}
            onChange={value => {
              if (wearableResult) {
                setWearableResult(undefined);
              }
              if (rawData && Array.isArray(rawData)) {
                setRawData(rawData?.map(raw => ({ ...raw, selected: false })));
              }
              setValue(value);
              setFormValue(key, value as any, element);
            }}
          />
          {error && <ErrorText>{error}</ErrorText>}
        </>
      ) : (
        <FormWearableFieldView>
          <TouchableWithoutFeedback
            onPress={() => {
              textInputRef?.current?.focus();
            }}
          >
            <StyledContainer>
              {isLoading ? (
                <ActivityIndicator color={Colors.primaryPurple} />
              ) : (
                <>
                  {Platform.OS === 'web' && (
                    <StyledText
                      onLayout={event => {
                        setTextWidth(event.nativeEvent.layout.width);
                      }}
                      style={{ position: 'absolute', opacity: 0, zIndex: -1 }}
                    >
                      {value}
                    </StyledText>
                  )}
                  <StyledTextInput
                    testID={elementIDs.formWearableTextfieldID}
                    maxLength={dataType === 'text' ? undefined : 10}
                    style={Platform.OS === 'web' ? { width: textWidth } : {}}
                    ref={textInputRef}
                    blurOnSubmit={true}
                    value={`${value ? value : ''}`}
                    onChangeText={text => {
                      if (wearableResult) {
                        setWearableResult(undefined);
                      }
                      if (rawData && Array.isArray(rawData)) {
                        setRawData(
                          rawData?.map(raw => ({ ...raw, selected: false }))
                        );
                      }
                      if (dataType === 'text') {
                        setValue(text);
                        setFormValue(key, text as any, element);
                        setError(undefined);
                        return;
                      }
                      const newText = text?.replace(/ /, '');
                      const bloodPressureRegex = /^\d{1,3}\/\d{1,3}$/;
                      setValue(newText);
                      if (
                        newText &&
                        wearableConfig?.dataSource !== 'Blood Pressure' &&
                        !isNaN(+newText)
                      ) {
                        setFormValue(key, +newText as any, element);
                        setError(undefined);
                      } else if (
                        wearableConfig?.dataSource === 'Blood Pressure' &&
                        bloodPressureRegex.test(newText)
                      ) {
                        setFormValue(key, newText as any, element);
                        setError(undefined);
                      } else {
                        if (newText) {
                          setError('Invalid Value');
                        } else {
                          setError(undefined);
                        }
                        setFormValue(key, undefined as any, element);
                      }
                    }}
                    onFocus={() => {
                      setFocused(true);
                    }}
                    onBlur={() => {
                      setFocused(false);
                    }}
                    placeholderTextColor={Colors.placeholderColor}
                    keyboardType={
                      wearableConfig?.dataSource !== 'Blood Pressure'
                        ? 'numeric'
                        : 'default'
                    }
                  />
                  <StyledText>
                    {value && `${value}`?.length > 0
                      ? wearableConfig?.unit
                      : ''}
                  </StyledText>
                </>
              )}
            </StyledContainer>
          </TouchableWithoutFeedback>
          {error && <ErrorText>{error}</ErrorText>}
        </FormWearableFieldView>
      )}
      {hasFallback || Platform.OS === 'android' ? (
        <>
          {hasFallback && (
            <HelperText>
              Select a source for your{' '}
              {wearableConfig?.dataSource ||
                fitbit?.dataSource ||
                garmin?.dataSource ||
                (Platform.OS === 'android'
                  ? (googleFit?.dataSource ?? healthConnect?.dataSource)
                  : healthKit?.dataSource
                )?.toLowerCase()}{' '}
              data:
            </HelperText>
          )}
          {Platform.OS !== 'android' ? (
            <StyledIconsContainer>
              {fitbit?.dataSource && (
                <TouchableOpacity
                  onPress={() => {
                    fetchWearableData(
                      false,
                      initWearHelper(WearableType.FITBIT)
                    );
                  }}
                  disabled={wearableConfig?.source === 'Fitbit'}
                >
                  <StyledFitbitIcon
                    selected={wearableConfig?.source == 'Fitbit'}
                    source={require('../../../assets/wearable/icon_fitbit.png')}
                  />
                </TouchableOpacity>
              )}
              {garmin?.dataSource && (
                <TouchableOpacity
                  onPress={() => {
                    fetchWearableData(
                      false,
                      initWearHelper(WearableType.GARMIN)
                    );
                  }}
                  disabled={wearableConfig?.source === 'Garmin'}
                >
                  <StyledGarminIcon
                    selected={wearableConfig?.source == 'Garmin'}
                    source={require('../../../assets/wearable/icon_garmin.png')}
                  />
                </TouchableOpacity>
              )}
              {oura?.dataSource && (
                <TouchableOpacity
                  onPress={() => {
                    fetchWearableData(false, initWearHelper(WearableType.OURA));
                  }}
                  disabled={wearableConfig?.source === 'Oura'}
                >
                  <StyledGarminIcon
                    selected={wearableConfig?.source == 'Oura'}
                    source={require('../../../assets/wearable/icon_oura.png')}
                  />
                </TouchableOpacity>
              )}
              {Platform.OS === 'ios' && healthKit?.dataSource && (
                <TouchableOpacity
                  onPress={() => {
                    fetchWearableData(
                      false,
                      initWearHelper(WearableType.HEALTH_KIT)
                    );
                  }}
                  disabled={wearableConfig?.source === 'HealthKit'}
                >
                  <StyledIcon
                    selected={wearableConfig?.source == 'HealthKit'}
                    source={require('../../../assets/wearable/icon_health_kit.png')}
                  />
                </TouchableOpacity>
              )}
            </StyledIconsContainer>
          ) : (
            <StyledIconTextContainer>
              {fitbit?.dataSource && (
                <>
                  <StyledTouchableIcon
                    onPress={async () => {
                      setError(undefined);
                      setHelperText(undefined);
                      clearValues();
                      if (fitbitAuthorized) {
                        fetchWearableData(
                          false,
                          initWearHelper(WearableType.FITBIT)
                        );
                      } else {
                        setWearableConfig({
                          source: 'Fitbit',
                          ...fitbit
                        } as WearableConfig);
                        setShowConnectModal(true);
                      }
                    }}
                    disabled={wearableConfig?.source === 'Fitbit'}
                  >
                    <StyledSelectedIndicator
                      selected={wearableConfig?.source == 'Fitbit'}
                    />
                    <StyledFitbitIconWithWordMark
                      resizeMode='contain'
                      source={require('../../../assets/wearable/icon_fitbit_text.png')}
                    />
                  </StyledTouchableIcon>
                  <Spacer size='large' />
                </>
              )}
              {garmin?.dataSource && (
                <>
                  <StyledTouchableIcon
                    onPress={async () => {
                      setError(undefined);
                      setHelperText(undefined);
                      clearValues();
                      const connected = user?.garminConfig;
                      if (connected) {
                        fetchWearableData(
                          false,
                          initWearHelper(WearableType.GARMIN)
                        );
                      } else {
                        setWearableConfig({
                          source: 'Garmin',
                          ...garmin
                        } as WearableConfig);
                        setShowConnectModal(true);
                      }
                    }}
                    disabled={wearableConfig?.source === 'Garmin'}
                  >
                    <StyledSelectedIndicator
                      selected={wearableConfig?.source == 'Garmin'}
                    />
                    <StyledGarminIconWithWordMark
                      resizeMode='contain'
                      source={require('../../../assets/wearable/icon_garmin_text.png')}
                    />
                  </StyledTouchableIcon>
                  <Spacer size='large' />
                </>
              )}
              {oura?.dataSource && (
                <>
                  <StyledTouchableIcon
                    onPress={async () => {
                      setError(undefined);
                      setHelperText(undefined);
                      clearValues();
                      if (ouraAccessToken) {
                        fetchWearableData(
                          false,
                          initWearHelper(WearableType.OURA)
                        );
                      } else {
                        setWearableConfig({
                          source: 'Oura',
                          ...oura
                        } as WearableConfig);
                        setShowConnectModal(true);
                      }
                    }}
                    disabled={wearableConfig?.source === 'Oura'}
                  >
                    <StyledSelectedIndicator
                      selected={wearableConfig?.source == 'Oura'}
                    />
                    <StyledOuraconWithWordMark
                      resizeMode='contain'
                      source={require('../../../assets/wearable/icon_oura_text.png')}
                    />
                  </StyledTouchableIcon>
                  <Spacer size='large' />
                </>
              )}
              {googleFit?.dataSource && (
                <>
                  <StyledTouchableIcon
                    onPress={async () => {
                      setError(undefined);
                      setHelperText(undefined);
                      clearValues();
                      const connected = await AsyncStorage.getItem(
                        'google-fit-connected'
                      );
                      if (connected) {
                        fetchWearableData(
                          false,
                          initWearHelper(WearableType.GOOGLE_FIT)
                        );
                      } else {
                        setWearableConfig({
                          source: 'Google Fit',
                          ...googleFit
                        } as WearableConfig);
                        setShowConnectModal(true);
                      }
                    }}
                    disabled={wearableConfig?.source === 'Google Fit'}
                  >
                    <StyledSelectedIndicator
                      selected={wearableConfig?.source === 'Google Fit'}
                    />
                    <StyledGoogleFitIconWithWordMark
                      resizeMode='contain'
                      source={require('../../../assets/wearable/icon_google_fit_text.png')}
                    />
                  </StyledTouchableIcon>
                  <Spacer size='large' />
                </>
              )}
              {healthConnect?.dataSource && (
                <StyledTouchableIcon
                  onPress={async () => {
                    const clear = () => {
                      setError(undefined);
                      setHelperText(undefined);
                      clearValues();
                    };
                    clear();
                    setWearableConfig({
                      source: 'Health Connect',
                      ...healthConnect
                    } as WearableConfig);
                    const isInstalled = await Linking.canOpenURL(
                      'healthconnect://onboarding'
                    );
                    if (isInstalled) {
                      const healthConnectAuth = new GoogleHealthConnect(
                        user?.id || 0
                      );
                      const initialized = await healthConnectAuth.authorize();
                      if (initialized) {
                        let grantedPermissions =
                          await healthConnectAuth.getGrantedPermissions();
                        const firstTimeCheck = await AsyncStorage.getItem(
                          'first-time-permission-check'
                        );
                        if (
                          firstTimeCheck === null &&
                          grantedPermissions.length === 0
                        ) {
                          setShowConnectModal(true);
                        } else {
                          if (grantedPermissions.length === 0) {
                            setShowConnectModal(true);
                          } else {
                            initWearHelper(WearableType.HEALTH_CONNECT)?.then(
                              wearHelper => {
                                fetchWearableData(false, wearHelper);
                              }
                            );
                          }
                        }
                      }
                    } else {
                      Alert.alert(
                        'Health Connect',
                        'The Google Health Connect app is required to fetch your health data',
                        [
                          {
                            text: 'Install',
                            onPress: async () => {
                              await Linking.openURL(
                                'market://details?id=com.google.android.apps.healthdata'
                              );
                              clear();
                            }
                          },
                          {
                            text: 'Cancel',
                            onPress: () => {
                              clear();
                            }
                          }
                        ]
                      );
                    }
                  }}
                  disabled={wearableConfig?.source === 'Health Connect'}
                >
                  <StyledSelectedIndicator
                    selected={wearableConfig?.source === 'Health Connect'}
                  />
                  <StyledHealthConnectIconWithWordMark
                    resizeMode='contain'
                    source={require('../../../assets/wearable/icon_health_connect_text.png')}
                  />
                </StyledTouchableIcon>
              )}
            </StyledIconTextContainer>
          )}
        </>
      ) : (
        <></>
      )}
      {['Fitbit', 'Garmin'].includes(wearableConfig?.source || '') &&
      rawData &&
      wearableConfig?.dataSource === 'Sleep' &&
      Array.isArray(rawData) ? (
        <>
          <HelperText>{`Sleep Stages:`}</HelperText>
          {rawData?.map((data: any, index) => {
            const sleepStage =
              data.sleepStage !== 'REM'
                ? toTitleCase(data.sleepStage)
                : data.sleepStage;
            const value = +data.value;
            const hour = Math.floor(value);
            const minute = Math.round((value - hour) * 60); // * 60);
            return (
              <StyledItemContainer key={index}>
                <StyledTouchable
                  onPress={() => {
                    const newData = [...rawData];
                    newData[index].selected = !newData[index].selected;
                    setRawData(newData);
                    const value = Number(
                      (
                        newData
                          ?.filter(data => data.selected)
                          ?.reduce((result: number, item: FormWearableData) => {
                            if (typeof item.value !== 'number') {
                              return result;
                            }
                            return result + item.value;
                          }, 0) || 0
                      ).toFixed(2)
                    );
                    if (newData.some(d => d.selected)) {
                      setValue(value);
                      setFormValue(key, value as any, element);
                    } else {
                      setValue('');
                      setFormValue(key, undefined as any, element);
                    }
                  }}
                >
                  <StyledBox touched={data.selected} />
                  <ListItemText>{`${sleepStage} (${hour > 0 ? `${hour}h` : ''}${
                    hour > 0 && minute > 0 ? ' ' : ''
                  }${minute > 0 ? `${minute}min` : ''})`}</ListItemText>
                </StyledTouchable>
              </StyledItemContainer>
            );
          })}
        </>
      ) : (
        <></>
      )}

      {['HealthKit', 'Google Fit', 'Health Connect'].includes(
        wearableConfig?.source || ''
      ) &&
      wearableResults &&
      wearableResults.length > 0 &&
      wearableConfig?.dataSource === 'Sleep' &&
      Array.isArray(rawData) ? (
        <>
          <HelperText>Source:</HelperText>
          {wearableResults?.map((data: any, index) => {
            const value = +data.value;
            const hour = Math.floor(value);
            const minute = Math.round((value - hour) * 60);
            return (
              <StyledItemContainer key={index}>
                <StyledTouchable
                  onPress={() => {
                    if (wearableResult?.sourceName === data.sourceName) {
                      setWearableResult(undefined);
                      setValue('');
                      setFormValue(key, undefined as any, element);
                    } else {
                      setWearableResult(data);
                      setValue(value);
                      setFormValue(key, value as any, element);
                    }
                  }}
                >
                  <StyledBox
                    touched={wearableResult?.sourceName === data.sourceName}
                  />
                  <ListItemText>{`${data.sourceName} (${
                    hour > 0 ? `${hour}h` : ''
                  }${hour > 0 && minute > 0 ? ' ' : ''}${
                    minute > 0 ? `${minute}min` : ''
                  })`}</ListItemText>
                </StyledTouchable>
              </StyledItemContainer>
            );
          })}
        </>
      ) : (
        <></>
      )}
      {!isLoading && wearableConfig?.source ? (
        <HelperText>
          {(helperText ||
            `Data populated from ${
              wearableResult?.sourceName || wearableConfig?.source
            } ${
              wearableResult?.startDate
                ? `from ${format(
                    wearableResult.startDate,
                    'MMM d, yyyy h:mm a'
                  )}${
                    wearableResult?.endDate
                      ? ` to ${format(
                          wearableResult.endDate,
                          'MMM d, yyyy h:mm a'
                        )}`
                      : ''
                  }`
                : 'when available'
            }. You can adjust the value if necessary.`) +
            ` ${(() => {
              switch (wearableConfig?.dataPointOccurrence) {
                case DataPointOccurrence.Intended_Start_Date:
                  return `Looking for data as of ${
                    intendedStartDate
                      ? format(intendedStartDate, 'MMM d, yyyy')
                      : 'N/A'
                  }.`;
                case DataPointOccurrence.Last_Recorded:
                  return 'Getting last recorded data.';
                case DataPointOccurrence.Today:
                  return `Getting today's data.`;
                case DataPointOccurrence.Yesterday:
                  return `Getting yesterday's data.`;
              }
            })()}`}
        </HelperText>
      ) : (
        <></>
      )}
      {Platform.OS === 'ios' &&
        wearableConfig?.source === 'HealthKit' &&
        helperText?.includes('No data found') && (
          <Button
            size='small'
            disabled={isLoading || fitbitLoading ? true : false}
            onPress={() => {
              navigation.navigate('ManageWearableNav');
            }}
          >
            Manage Apple Health
          </Button>
        )}
      {Platform.OS !== 'android' &&
      wearableConfig?.source === 'Fitbit' &&
      !fitbitAuthorized ? (
        <Button
          size='small'
          disabled={isLoading || fitbitLoading ? true : false}
          endIcon={
            fitbitLoading ? (
              <ActivityIndicator size='small' color='white' />
            ) : (
              <></>
            )
          }
          onPress={() => {
            setFitbitLoading(true);
            promptAsync();
          }}
        >
          Connect Fitbit Account
        </Button>
      ) : (
        <></>
      )}
      {Platform.OS !== 'android' &&
      wearableConfig?.source === 'Garmin' &&
      !garminAuthorized ? (
        <Button
          size='small'
          disabled={isLoading ? true : false}
          endIcon={
            isLoading ? <ActivityIndicator size='small' color='white' /> : <></>
          }
          onPress={() => {
            setLoading(true);
            requestToken()
              ?.then(garminToken => {
                if (garminToken) {
                  setUser({
                    ...user,
                    garminConfig: {
                      accessToken: garminToken.key,
                      accessTokenSecret: garminToken.secret
                    }
                  } as User);
                  handleGarminConnectSuccess();
                } else {
                  setLoading(false);
                }
              })
              ?.catch(() => setLoading(false));
          }}
        >
          Connect Garmin Account
        </Button>
      ) : (
        <></>
      )}
      {Platform.OS !== 'android' &&
      wearableConfig?.source === 'Oura' &&
      !ouraAccessToken ? (
        <Button
          size='small'
          disabled={isLoading || ouraLoading ? true : false}
          endIcon={
            ouraLoading ? (
              <ActivityIndicator size='small' color='white' />
            ) : (
              <></>
            )
          }
          onPress={async () => {
            const accessToken = await connectOura();
            if (accessToken) {
              const wearableHelper = await initWearHelper(WearableType.OURA);
              fetchWearableData(false, wearableHelper, false);
            }
          }}
        >
          Connect Oura Account
        </Button>
      ) : (
        <></>
      )}
      {!isLoading &&
        garminAuthorized &&
        wearableConfig?.source === 'Garmin' && (
          <Button
            size='small'
            disabled={isLoading}
            endIcon={
              isLoading ? (
                <ActivityIndicator size='small' color='white' />
              ) : (
                <></>
              )
            }
            onPress={() => {
              fetchWearableData(false, initWearHelper(WearableType.GARMIN));
            }}
          >
            Resync
          </Button>
        )}
      {showConnectModal && (
        <Modal animationType='fade' transparent>
          <StyledDimContainer>
            <StyledDialog>
              <StyledIconButton
                onPress={() => {
                  if (focused) {
                    Keyboard.dismiss();
                  }
                  setShowConnectModal(false);
                  clearValues();
                }}
              >
                <Feather name='x' size={28} color='black' />
              </StyledIconButton>
              <StyledLogo
                resizeMode='contain'
                source={
                  wearableConfig?.source === 'Fitbit'
                    ? require('../../../assets/wearable/icon_fitbit_black.png')
                    : wearableConfig?.source === 'Oura'
                      ? require('../../../assets/wearable/icon_oura_black.png')
                      : wearableConfig?.source === 'Garmin'
                        ? require('../../../assets/wearable/icon_garmin.png')
                        : wearableConfig?.source === 'Health Connect'
                          ? require('../../../assets/wearable/icon_health_connect.png')
                          : require('../../../assets/wearable/icon_google_fit.png')
                }
              />
              <Spacer size='large' />
              <B color='dark'>
                {`Connect to ${
                  wearableConfig?.source || 'Google Fit'
                } to sync your data to Chloe.`}
              </B>
              <Spacer size={6} />
              {isLoading || fitbitLoading || ouraLoading ? (
                <ActivityIndicator />
              ) : (
                <TouchableOpacity
                  onPress={async () => {
                    if (wearableConfig?.source === 'Fitbit') {
                      promptAsync();
                    } else if (wearableConfig?.source === 'Oura') {
                      setShowConnectModal(false);
                      setOpenAndroidAuraConnect(true);
                    } else if (wearableConfig?.source === 'Garmin') {
                      setLoading(true);
                      try {
                        const garminToken = await requestToken();
                        if (garminToken) {
                          handleGarminConnectSuccess();
                        } else {
                          setLoading(false);
                        }
                      } catch (e: any) {
                        setLoading(false);
                        setError(
                          e?.message || 'Something went wrong, try again.'
                        );
                      }
                    } else if (wearableConfig?.source === 'Health Connect') {
                      const healthConnectAuth = new GoogleHealthConnect(
                        user?.id || 0
                      );
                      const initialized = await healthConnectAuth.authorize();
                      let fetch = true;
                      if (initialized) {
                        let grantedPermissions =
                          await healthConnectAuth.getGrantedPermissions();
                        const firstTimeCheck = await AsyncStorage.getItem(
                          'first-time-permission-check'
                        );
                        if (
                          firstTimeCheck === null &&
                          grantedPermissions.length === 0
                        ) {
                          const permissions =
                            await healthConnectAuth.checkPermissions();
                          if (permissions.length) {
                            AsyncStorage.setItem(
                              'first-time-permission-check',
                              'false'
                            );
                          } else {
                            setError('');
                            setHelperText(undefined);
                            clearValues();
                            fetch = false;
                          }
                          setShowConnectModal(false);
                        } else {
                          setShowConnectModal(false);
                          healthConnectAuth.openHealthConnectSettings();
                        }
                        if (fetch) {
                          const wearHelper = await initWearHelper(
                            WearableType.HEALTH_CONNECT
                          );
                          fetchWearableData(false, wearHelper, true);
                        }
                      }
                    } else {
                      const wearHelper = await initWearHelper(
                        WearableType.GOOGLE_FIT
                      );
                      fetchWearableData(false, wearHelper, true);
                    }
                  }}
                >
                  {wearableConfig?.source === 'Health Connect' ? (
                    <View pointerEvents='none'>
                      <Button>Connect</Button>
                    </View>
                  ) : (
                    <StyledSignInImage
                      resizeMode='contain'
                      source={
                        wearableConfig?.source === 'Fitbit'
                          ? require('../../../assets/wearable/sign-in-with-fitbit.png')
                          : wearableConfig?.source === 'Oura'
                            ? require('../../../assets/wearable/sign-in-with-oura.png')
                            : wearableConfig?.source === 'Garmin'
                              ? require('../../../assets/wearable/sign-in-with-garmin.png')
                              : require('../../../assets/wearable/sign-in-with-google.png')
                      }
                    />
                  )}
                </TouchableOpacity>
              )}
            </StyledDialog>
          </StyledDimContainer>
        </Modal>
      )}
      {openAndroidOuraConnect && (
        <WebViewModal
          height={900}
          scrollOnFieldClicked
          incognito={true}
          url={ouraAuthUrl}
          onClose={() => {
            setOpenAndroidAuraConnect(false);
            setWearableConfig(undefined);
          }}
          onNavigationStateChange={async stateNav => {
            if (stateNav.url.includes(config.redirectUri)) {
              const accessToken = await handleOuraAuthResult(stateNav);
              if (accessToken) {
                const wearableHelper = await initWearHelper(WearableType.OURA);
                fetchWearableData(false, wearableHelper, false);
                setShowConnectModal(false);
              }
              setOpenAndroidAuraConnect(false);
            }
          }}
          title={'Connect Oura'}
        />
      )}
    </View>
  );
};

type DataConfig = {
  dataSource: string;
  dataPointOccurrence: DataPointOccurrence;
  category?: string;
  unit?: string;
};

type FormWearableConfig = {
  healthKit: DataConfig;
  googleFit: DataConfig;
  healthConnect: DataConfig;
  fitbit: DataConfig;
  garmin: DataConfig;
  oura: DataConfig;
  rawDataSource: string;
  dataType: string;
};

export const FormWearablePlugin: FormElementPlugin<any, FormWearableConfig> = {
  name: 'Wearable',
  type: 'FormWearable',
  dataTypes: ['integer', 'numeric', 'text'],
  init: (type, _, previousElement) => ({
    type,
    dataType: 'text',
    title: previousElement?.title || 'Question',
    key: previousElement?.key || 'question'
  }),
  element: FormWearable
};
